import type { ComponentType } from 'react';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import AlignVerticalCenterIcon from '@mui/icons-material/AlignVerticalCenter';
import CycloneIcon from '@mui/icons-material/Cyclone';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import InsightsIcon from '@mui/icons-material/Insights';
import CalculateIcon from '@mui/icons-material/Calculate';
import { Feed } from '@mui/icons-material';
import BellCurve from 'icons/BellCurve';
import TapeIcon from 'icons/TapeIcon';
import { CanvasContentId } from 'types/canvas';
import { ProductType } from 'types/shared';
import { SubLevel } from 'types/user';
import { ALL_INDICES_SYMS } from 'util/indices';
import { ComboSymbol } from 'config/hiro';

/**
 * Categories mirror the SpotGamma product pages each component originated
 * on, so users find things in the same place they already know them from.
 */
export type ComponentCategoryId =
  | 'market-overview'
  | 'equityhub'
  | 'volatility-dashboard'
  | 'trace'
  | 'hiro'
  | 'tape'
  | 'scanners'
  | 'founders-notes'
  | 'options-calculator';

export interface ComponentCategory {
  id: ComponentCategoryId;
  label: string;
  /**
   * Accepts MUI built-in icons and our local `SvgIcon`-wrapped custom
   * icons (BellCurve, TapeIcon) — both render the same way under
   * `<Icon sx={...} />`.
   */
  icon: ComponentType<SvgIconProps>;
  /**
   * Drives the access check + upsell. Each category maps to the product
   * page where its components originated, so the existing
   * `useAuth().hasAccessToProduct(productType)` rules apply.
   */
  productType: ProductType;
}

export const COMPONENT_CATEGORIES: readonly ComponentCategory[] = [
  {
    id: 'market-overview',
    label: 'Market Overview',
    icon: LeaderboardIcon,
    productType: ProductType.INDICES,
  },
  {
    id: 'equityhub',
    label: 'Equity Hub',
    icon: AlignVerticalCenterIcon,
    productType: ProductType.EQUITYHUB,
  },
  {
    id: 'volatility-dashboard',
    label: 'Volatility Dashboard',
    icon: BellCurve,
    productType: ProductType.IMPLIED_VOL,
  },
  {
    id: 'trace',
    label: 'Trace',
    icon: CycloneIcon,
    productType: ProductType.TRACE,
  },
  {
    id: 'hiro',
    label: 'HIRO',
    icon: InsightsIcon,
    productType: ProductType.HIRO,
  },
  {
    id: 'tape',
    label: 'Tape',
    icon: TapeIcon,
    productType: ProductType.TAPE,
  },
  {
    id: 'scanners',
    label: 'Scanners',
    icon: QrCodeScannerIcon,
    productType: ProductType.SCANNERS,
  },
  {
    id: 'founders-notes',
    label: "Founder's Notes",
    icon: Feed,
    productType: ProductType.FOUNDERS_NOTES,
  },
  {
    id: 'options-calculator',
    label: 'Options Calculator',
    icon: CalculateIcon,
    productType: ProductType.OPTIONS_CALCULATOR,
  },
];

/**
 * Live Prices accepts the indices plus the S&P futures combo sym. Same
 * ordering CanvasFuturesChart uses for its selector dropdown so the chart
 * options and the broadcast whitelist stay in lockstep.
 */
export const LIVE_PRICES_SYMS: readonly string[] = [
  ComboSymbol.ES_F,
  ...ALL_INDICES_SYMS,
];

/**
 * Per-component access override, for components gated more tightly than their
 * category. When omitted, the category's `productType` alone governs access.
 */
export interface ComponentAccess {
  /** Product required; omit to apply no product check (gate on sub level only). */
  productType?: ProductType;
  /** Requires the user's sub level strictly above this (Synthetic OI → `PRO`). */
  minSubLevelExclusive?: SubLevel;
  /** Label for the lock/upsell, used instead of the category label. */
  upsellLabel?: string;
}

export interface ComponentMeta {
  category: ComponentCategoryId;
  /** Access override; see {@link ComponentAccess}. */
  access?: ComponentAccess;
  /**
   * `true` when this component bound to exactly one ticker via `config.sym`
   * (HIRO, GEX, Composite View, Term Structure, etc.). Single-symbol
   * components are eligible for symbol-sharing groups — changing the sym
   * on one broadcasts to every other group member. `false` for index-only
   * tiles, scanners, flow tape, and calendars whose data isn't keyed by a
   * user-selectable single symbol.
   */
  singleSymbol: boolean;
  /**
   * Which symbol universe this component can render, used to compute a group's
   * allowed symbols from its members:
   *   - `'equity'` (default): any equity ticker (EquityHub / implied-vol etc.).
   *     Equities are the superset.
   *   - `'hiro'`: the HIRO universe — a subset of equities PLUS HIRO-only
   *     specials (indices, futures combos) not in the equities list.
   * A group's allowed set is the intersection of its members' universes, so a
   * HIRO-only group allows all HIRO syms while an equity-only group allows all
   * equities. Ignored when `supportedSyms` is set (that whitelist wins).
   */
  symbolUniverse?: 'hiro' | 'equity';
  /**
   * Whitelist of symbols this component can render. When set, a sym
   * broadcast skips this component if the new sym isn't in the list (the
   * component keeps its previous sym instead of receiving invalid data).
   * Leave undefined for components that accept any equity ticker.
   */
  supportedSyms?: readonly string[];
  /**
   * Cap on how many instances of this component a single workspace can
   * contain. Heavy charts (Trace, Tape) opt in to avoid straining the
   * browser. Leave undefined for unlimited.
   */
  maxPerWorkspace?: number;
  /**
   * Restricts this component to SpotGamma staff (`hasInternalDashAccessState`).
   * Unlike `access`, this isn't a subscription tier a customer can upgrade
   * into, so it's a hard filter — the component is omitted from the add
   * dialog entirely for everyone else, with no lock/upsell shown.
   */
  internalOnly?: boolean;
}

export const CANVAS_COMPONENT_META: Record<CanvasContentId, ComponentMeta> = {
  // ── Market Overview (Indices + homepage) ────────────────────────────────
  [CanvasContentId.GAMMA_LEVELS_CHART]: {
    category: 'market-overview',
    singleSymbol: true,
    supportedSyms: ALL_INDICES_SYMS,
  },
  [CanvasContentId.GAMMA_MODEL_CHART]: {
    category: 'market-overview',
    singleSymbol: true,
    supportedSyms: ALL_INDICES_SYMS,
  },
  [CanvasContentId.COMBO_STRIKES]: {
    category: 'market-overview',
    singleSymbol: true,
    supportedSyms: ALL_INDICES_SYMS,
  },
  [CanvasContentId.EXP_CONCENTRATION_CHART]: {
    category: 'market-overview',
    // Multi-symbol component: lets the user pick any subset of indices via
    // a checkbox dropdown. Doesn't participate in symbol-sharing groups
    // because there's no single ticker to broadcast.
    singleSymbol: false,
  },
  [CanvasContentId.INDEX_LEVELS]: {
    category: 'market-overview',
    singleSymbol: false,
  },
  [CanvasContentId.INDEX_METRICS]: {
    category: 'market-overview',
    singleSymbol: false,
  },
  [CanvasContentId.SG_LEVELS]: {
    category: 'market-overview',
    singleSymbol: false,
  },
  [CanvasContentId.SUPP_RESISTANCE_LEVELS]: {
    category: 'market-overview',
    singleSymbol: false,
  },
  [CanvasContentId.AT_SPOTGAMMA]: {
    category: 'market-overview',
    singleSymbol: false,
  },
  [CanvasContentId.LIVE_PRICES_CHART]: {
    category: 'market-overview',
    singleSymbol: true,
    supportedSyms: LIVE_PRICES_SYMS,
  },
  [CanvasContentId.OCC_CHART]: {
    category: 'market-overview',
    singleSymbol: false,
  },
  [CanvasContentId.EQUITY_PUT_CALL_RATIO_CHART]: {
    category: 'market-overview',
    singleSymbol: false,
  },
  [CanvasContentId.ZERO_DTE_EQUITY_VOL_CHART]: {
    category: 'market-overview',
    singleSymbol: false,
  },
  [CanvasContentId.EARNINGS]: {
    category: 'market-overview',
    singleSymbol: false,
  },
  [CanvasContentId.EARNINGS_CHART]: {
    category: 'market-overview',
    singleSymbol: false,
  },
  [CanvasContentId.MACRO_ECONOMIC_CALENDAR]: {
    category: 'market-overview',
    singleSymbol: false,
  },

  // ── EquityHub (per-symbol equity views) ─────────────────────────────────
  [CanvasContentId.COMPOSITE_VIEW_CHART]: {
    category: 'equityhub',
    singleSymbol: true,
    symbolUniverse: 'equity',
  },
  [CanvasContentId.SYNTH_COMPOSITE_VIEW_CHART]: {
    category: 'equityhub',
    access: {
      minSubLevelExclusive: SubLevel.ALPHA,
      upsellLabel: 'Synthetic OI',
    },
    singleSymbol: true,
    symbolUniverse: 'equity',
  },
  [CanvasContentId.SYNTH_OI_PUT_CALL_IMPACT_CHART]: {
    category: 'equityhub',
    access: {
      minSubLevelExclusive: SubLevel.ALPHA,
      upsellLabel: 'Synthetic OI',
    },
    singleSymbol: true,
    symbolUniverse: 'equity',
  },
  [CanvasContentId.TOTAL_OI_PUT_CALL_IMPACT_CHART]: {
    category: 'equityhub',
    singleSymbol: true,
    symbolUniverse: 'equity',
  },

  // ── Volatility Dashboard (Implied Vol page) ─────────────────────────────
  [CanvasContentId.VOL_SKEW]: {
    category: 'volatility-dashboard',
    singleSymbol: true,
    symbolUniverse: 'hiro',
  },
  [CanvasContentId.TERM_STRUCTURE]: {
    category: 'volatility-dashboard',
    singleSymbol: true,
    symbolUniverse: 'hiro',
  },
  [CanvasContentId.FIXED_STRIKE_MATRIX]: {
    category: 'volatility-dashboard',
    singleSymbol: true,
    symbolUniverse: 'hiro',
  },
  [CanvasContentId.VIX_CHART]: {
    category: 'volatility-dashboard',
    // VIX-only; not user-selectable across other symbols.
    singleSymbol: false,
  },

  // ── Trace ───────────────────────────────────────────────────────────────
  [CanvasContentId.TRACE]: {
    category: 'trace',
    // Canvas Trace is hard-coded to SPX (see CanvasTrace.tsx), so it isn't
    // a participating member of the symbol-sharing groups feature.
    singleSymbol: false,
    maxPerWorkspace: 2,
  },

  // ── Hiro ───────────────────────────────────────────────────────────────
  [CanvasContentId.HIRO_CHART]: {
    // Own product → own dialog section + HIRO-access gating.
    category: 'hiro',
    symbolUniverse: 'hiro',
    singleSymbol: true,
  },

  // ── Tape ────────────────────────────────────────────────────────────────
  [CanvasContentId.FLOW_DATA]: {
    category: 'tape',
    singleSymbol: false,
    maxPerWorkspace: 2,
    symbolUniverse: 'hiro',
  },

  // ── Scanners ────────────────────────────────────────────────────────────
  [CanvasContentId.EQUITIES_SCANNER]: {
    category: 'scanners',
    singleSymbol: false,
  },
  [CanvasContentId.SCANNER_SQUEEZE_CANDIDATES]: {
    category: 'scanners',
    singleSymbol: false,
  },
  [CanvasContentId.SCANNER_VRP_CANDIDATES]: {
    category: 'scanners',
    singleSymbol: false,
  },
  [CanvasContentId.STRATEGY_COMPASS]: {
    category: 'scanners',
    singleSymbol: false,
  },

  // ── Founder's Notes ─────────────────────────────────────────────────────
  [CanvasContentId.FOUNDERS_NOTES]: {
    category: 'founders-notes',
    singleSymbol: false,
  },

  // ── Options Calculator ──────────────────────────────────────────────────
  [CanvasContentId.OPTIONS_CALCULATOR]: {
    category: 'options-calculator',
    singleSymbol: true,
    symbolUniverse: 'hiro',
    internalOnly: true,
  },
};
